'use client';

import type React from 'react';
import { useState, useRef, useEffect } from 'react';

import { cn } from '@skybeam/shared-utils';

export interface LogoSliderProps {
  gradientFromColor?: string;
  includeButtons?: boolean;
  logos: {
    icon: React.ReactNode;
    testId?: string;
    type: 'streaming' | 'traditional' | 'both';
  }[];
  rootTestId?: string;
  trackTestId?: string;
  viewportTestId?: string;
}

export const LogoSlider: React.FC<LogoSliderProps> = ({
  gradientFromColor = 'from-blue-600',
  includeButtons = true,
  logos,
  rootTestId,
  trackTestId,
  viewportTestId,
}) => {
  const [activeType, setActiveType] = useState<
    'streaming' | 'traditional' | null
  >(null);
  const sliderRef = useRef<HTMLUListElement>(null);
  const animationRef = useRef<number | undefined>(undefined);
  const positionRef = useRef(0);

  const handleClick = (type: 'streaming' | 'traditional' | null) => () => {
    setActiveType((prev) => (prev === type ? null : type));
  };

  const duplicatedLogos = [...logos, ...logos];

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const updateAnimation = () => {
      const totalWidth = slider.scrollWidth;
      const firstSetWidth = totalWidth / 2;

      const speed = firstSetWidth / (55 * 40);

      const animate = () => {
        positionRef.current -= speed;

        if (Math.abs(positionRef.current) >= firstSetWidth) {
          positionRef.current = 0;
        }

        slider.style.transform = `translateX(${positionRef.current}px)`;
        animationRef.current = requestAnimationFrame(animate);
      };

      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      positionRef.current = 0;
      animationRef.current = requestAnimationFrame(animate);
    };

    const resizeObserver = new ResizeObserver(() => {
      updateAnimation();
    });

    resizeObserver.observe(slider);

    updateAnimation();

    return () => {
      resizeObserver.disconnect();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div
      data-test={rootTestId}
      className="tablet:gap-y-3.5 laptop:gap-y-10 desktop:gap-y-12 flex flex-col gap-y-[33px]"
    >
      <div
        data-test={viewportTestId}
        className="relative max-h-18 overflow-hidden"
      >
        <ul
          data-test={trackTestId}
          ref={sliderRef}
          className="inline-flex items-center gap-x-10 whitespace-nowrap [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {duplicatedLogos.map(({ icon, testId, type }, index) => {
            const renderBorder =
              type === activeType || (type === 'both' && activeType !== null);
            return (
              <li
                key={index}
                data-test={testId}
                className={cn(
                  'relative flex h-[72px] shrink-0 items-center justify-center border border-transparent px-3',
                  renderBorder ? 'border-white' : '',
                )}
              >
                <div className="relative">{icon}</div>
              </li>
            );
          })}
        </ul>

        <div
          className={`pointer-events-none absolute inset-y-0 left-0 w-16 bg-linear-to-r ${gradientFromColor} to-transparent`}
        />
        <div
          className={`pointer-events-none absolute inset-y-0 right-0 w-16 bg-linear-to-l ${gradientFromColor} to-transparent`}
        />
      </div>
      {includeButtons && (
        <div className="laptop:gap-x-[18px] laptop:text-xl flex justify-center gap-x-3.5 text-base text-white">
          <div
            className={cn(
              'laptop:py-3 laptop:px-7 laptop:hover:border-blue-100 laptop:hover:bg-blue-400 cursor-pointer rounded border border-blue-400 px-6 py-2.5 select-none',
              activeType === 'streaming' ? 'border-blue-100 bg-blue-400' : '',
            )}
            onClick={handleClick('streaming')}
          >
            Streaming
          </div>
          <div
            className={cn(
              'laptop:leading-[1.7] laptop:hover:border-blue-100 laptop:hover:bg-blue-400 cursor-pointer rounded border border-blue-400 px-6 py-2.5 select-none',
              activeType === 'traditional' ? 'border-blue-100 bg-blue-400' : '',
            )}
            onClick={handleClick('traditional')}
          >
            Local
          </div>
        </div>
      )}
    </div>
  );
};
